import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, AlertTriangle, RefreshCw } from "lucide-react";
import ParticleXBackground from "@/components/ParticleXBackground";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [noAccess, setNoAccess] = useState(false);
  const [codeMode, setCodeMode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const { login, loginWithGoogle, logout, requestLoginCode, loginWithCode } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success(t('auth.checkYourEmail'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setResendingEmail(false);
    }
  };

  const routeAfterLogin = async (user) => {
    const adminRoles = ['platform_admin', 'operations_manager', 'holding_admin'];
    if (user.email_verified === false && !adminRoles.includes(user.role)) {
      setCodeMode(false);
      setEmailNotVerified(true);
      return;
    }
    // Unified login: Doserne members have no AllStillas access until granted a role.
    const ALLSTILLAS_ROLES = ['platform_admin', 'operations_manager', 'holding_admin', 'webshop_admin', 'rentals_admin', 'client_admin', 'client_viewer'];
    const isSeller = Array.isArray(user.roles) && user.roles.includes('seller');
    const hasAccess = ALLSTILLAS_ROLES.includes(user.role) || isSeller;
    if (user.is_doserne_member && !hasAccess) {
      await logout();
      setCodeMode(false);
      setNoAccess(true);
      return;
    }
    toast.success(t('auth.welcomeBack'));
    const routes = {
      platform_admin: "/admin",
      operations_manager: "/admin",
      holding_admin: "/holding",
      team_leader: "/team-leader",
      worker: "/worker",
      client_admin: "/client",
      client_viewer: "/client",
      webshop_admin: "/admin/webshop",
      rentals_admin: "/admin/rentals",
    };
    navigate(routes[user.role] || (isSeller ? "/seller/dashboard" : "/"));
  };

  const handleRequestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    setSendingCode(true);
    try {
      await requestLoginCode(email);
      setCodeSent(true);
      toast.success(t('auth.codeSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);
    try {
      const user = await loginWithCode(email, code);
      await routeAfterLogin(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);

    try {
      const user = await login(email, password);
      await routeAfterLogin(user);
    } catch (error) {
      const detail = error.response?.data?.detail || t('auth.invalidCredentials');
      if (detail.includes("verify") || detail.includes("email")) {
        setEmailNotVerified(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  if (codeMode) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <ParticleXBackground opacity={0.4} />
        <div className="fixed inset-0 grid-bg opacity-20 pointer-events-none"></div>
        <div className="fixed top-4 right-4 z-50"><LanguageSwitcher /></div>
        <div className="relative w-full max-w-md">
          <Link to="/" className="flex items-center gap-1 justify-center mb-8">
            <div className="px-3 py-1.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
              <span className="text-[#23F7DD] font-bold text-xl font-['Unbounded']">All</span>
            </div>
            <span className="text-2xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
          </Link>
          <div className="glass-card p-8">
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white text-center mb-2">{t('auth.loginWithCodeTitle')}</h1>
            <p className="text-[#94A3B8] text-center mb-8">{codeSent ? t('auth.enterCodeDesc') : t('auth.loginWithCodeDesc')}</p>
            <form onSubmit={codeSent ? handleVerifyCode : handleRequestCode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code-email" className="text-[#94A3B8]">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                  <Input id="code-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" disabled={codeSent} className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20" required data-testid="code-email-input" />
                </div>
              </div>
              {codeSent && (
                <div className="space-y-2">
                  <Label htmlFor="code-input" className="text-[#94A3B8]">{t('auth.codeLabel')}</Label>
                  <Input id="code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="______" className="text-center tracking-[0.5em] text-lg bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20" required data-testid="code-input" />
                </div>
              )}
              <Button type="submit" disabled={loading || sendingCode} className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider" data-testid={codeSent ? "code-verify-btn" : "code-send-btn"}>
                {(loading || sendingCode) ? (
                  <span className="flex items-center gap-2"><span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>{t('common.loading')}</span>
                ) : (codeSent ? t('auth.login') : t('auth.sendCode'))}
              </Button>
            </form>
            {codeSent && (
              <button type="button" onClick={handleRequestCode} disabled={sendingCode} className="block w-full text-center text-sm text-[#94A3B8] hover:text-[#23F7DD] mt-4" data-testid="code-resend-btn">{t('auth.resendCode')}</button>
            )}
            <button type="button" onClick={() => { setCodeMode(false); setCodeSent(false); setCode(""); }} className="block w-full text-center text-sm text-[#94A3B8] hover:text-[#F7931A] mt-4" data-testid="code-back-btn">{t('auth.backToPassword')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      {/* Particle X Background */}
      <ParticleXBackground opacity={0.4} />
      
      {/* Background Grid */}
      <div className="fixed inset-0 grid-bg opacity-20 pointer-events-none"></div>

      {/* Language Switcher - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      {noAccess ? (
        <div className="relative w-full max-w-md">
          <div className="glass-card p-8 text-center" data-testid="no-access-card">
            <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-['Unbounded'] font-bold text-white mb-3" data-testid="no-access-title">
              {t('auth.noAccessTitle')}
            </h1>
            <p className="text-[#94A3B8] mb-8">{t('auth.noAccessBody')}</p>
            <Button
              type="button"
              onClick={() => { setNoAccess(false); setEmail(""); setPassword(""); }}
              className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider"
              data-testid="no-access-back-btn"
            >
              {t('auth.backToLogin')}
            </Button>
          </div>
        </div>
      ) : (
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1 justify-center mb-8">
          <div className="px-3 py-1.5 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
            <span className="text-[#23F7DD] font-bold text-xl font-['Unbounded']">All</span>
          </div>
          <span className="text-2xl font-bold font-['Unbounded'] neon-text-subtle">Stillas</span>
        </Link>

        {/* Login Card */}
        <div className="glass-card p-8">
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white text-center mb-2">
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-[#94A3B8] text-center mb-8">
            {t('auth.signIn')}
          </p>

          {/* Email Not Verified Warning */}
          {emailNotVerified && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-yellow-500 font-medium mb-1">{t('auth.emailNotVerified')}</h3>
                  <p className="text-[#94A3B8] text-sm mb-3">
                    {t('auth.checkYourEmail')}
                  </p>
                  <Button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    size="sm"
                    className="bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 border border-yellow-500/30"
                  >
                    {resendingEmail ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        {t('auth.resendVerification')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full mb-6 py-6 border-white/20 text-white hover:bg-white/5 hover:border-[#23F7DD]"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('auth.signInWith', { provider: 'Google' })}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#0A0A0A] text-[#94A3B8]">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#94A3B8]">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#475569]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#94A3B8]">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-11 bg-black/50 border-white/10 text-white placeholder:text-[#475569] focus:border-[#23F7DD] focus:ring-[#23F7DD]/20"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#94A3B8]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold uppercase tracking-wider"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin"></span>
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.login')
              )}
            </Button>
            
            <Link to="/forgot-password" className="block text-center text-sm text-[#94A3B8] hover:text-[#F7931A] mt-3">
              {t('auth.forgotPassword')}
            </Link>

            <button
              type="button"
              onClick={() => { setCodeMode(true); setCodeSent(false); setCode(""); }}
              className="block w-full text-center text-sm text-[#23F7DD] hover:text-[#70FFEF] mt-3 font-medium"
              data-testid="code-login-toggle"
            >
              {t('auth.loginWithCode')}
            </button>
          </form>

          <p className="mt-6 text-center text-[#94A3B8]">
            {t('auth.dontHaveAccount')}{" "}
            <Link to="/register" className="text-[#23F7DD] hover:underline font-medium">
              {t('auth.signUp')}
            </Link>
          </p>
        </div>
      </div>
      )}
    </div>
  );
}
